import { useRef, useEffect, useState, useContext } from "react";
import gsap from "gsap";
import { ModalContext } from "../../context/ModalContext";
import "../../styles/ComponentStyles/StrategyCallModal.css";
import Calendar from "../calender/Calendar.jsx";
import { createStrategyCall } from "../../services/stratergy.service";

const COUNTRY_CODES = [
  { code: "+91", country: "India", mobileLength: 10 },
  { code: "+1", country: "USA", mobileLength: 10 },
  { code: "+44", country: "UK", mobileLength: 10 },
  { code: "+61", country: "Australia", mobileLength: 9 },
  { code: "+81", country: "Japan", mobileLength: 10 },
  { code: "+86", country: "China", mobileLength: 11 },
  { code: "+33", country: "France", mobileLength: 9 },
  { code: "+49", country: "Germany", mobileLength: 11 },
  { code: "+39", country: "Italy", mobileLength: 10 },
  { code: "+34", country: "Spain", mobileLength: 9 },
  { code: "+31", country: "Netherlands", mobileLength: 9 },
  { code: "+41", country: "Switzerland", mobileLength: 9 },
  { code: "+43", country: "Austria", mobileLength: 10 },
  { code: "+45", country: "Denmark", mobileLength: 8 },
  { code: "+46", country: "Sweden", mobileLength: 9 },
  { code: "+47", country: "Norway", mobileLength: 8 },
  { code: "+48", country: "Poland", mobileLength: 9 },
  { code: "+55", country: "Brazil", mobileLength: 11 },
  { code: "+1", country: "Canada", mobileLength: 10 },
  { code: "+64", country: "New Zealand", mobileLength: 9 },
];

const StrategyCallModal = () => {
  const sheetRef = useRef(null);
  const rightPanelRef = useRef(null);
  const { closeStrategy, showToast } = useContext(ModalContext);

  // STEP CONTROL
  const [step, setStep] = useState("select"); // select | form
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [isStacked, setIsStacked] = useState(false);
  const leftPanelRef = useRef(null);
  const leftBodyRef = useRef(null);
  const [canExpand, setCanExpand] = useState(false);



 const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    countryCode: "+91",
    mobile: "",
  });


  const [formErrors, setFormErrors] = useState({
    fullName: "",
    email: "",  
    subject: "",
    mobile: "",
  });

  /* ---------------- FORMATTERS ---------------- */

  const formatDateForBackend = (dateStr) => {
    const [dd, mm, yyyy] = dateStr.split("/");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatTimeForBackend = (timeStr) => {
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":");

    if (modifier === "PM" && hours !== "12") hours = String(Number(hours) + 12);
    if (modifier === "AM" && hours === "12") hours = "00";

    return `${hours}:${minutes}`;
  };

  /* ---------------- ANIMATION ---------------- */

  useEffect(() => {
    gsap.fromTo(
      sheetRef.current,
      { y: 120, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.45, ease: "power3.out" }
    );

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const closeAll = () => {
    gsap.to(sheetRef.current, {
      y: 120,
      opacity: 0,
      duration: 0.3,
      onComplete: closeStrategy,
    });
  };

  useEffect(() => {
  const checkLayout = () => {
    setIsStacked(window.innerWidth <= 1024);
  };

  checkLayout();
  window.addEventListener("resize", checkLayout);
  return () => window.removeEventListener("resize", checkLayout);
}, []);

useEffect(() => {
  const checkOverflow = () => {
    if (!leftBodyRef.current || !leftPanelRef.current) return;

    const isOverflowing =
      leftBodyRef.current.scrollHeight >
      leftPanelRef.current.clientHeight;

    setCanExpand(isOverflowing);
  };

  checkOverflow();
  window.addEventListener("resize", checkOverflow);
  return () => window.removeEventListener("resize", checkOverflow);
}, [showMore]);



  /* ---------------- VALIDATION ---------------- */

  const validateForm = () => {
    let valid = true;
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = "Full name is required";
      valid = false;
    }

    if (!formData.subject.trim()) {
      errors.subject = "Subject is required";
      valid = false;
    }

    const country = COUNTRY_CODES.find(
      (c) => c.code === formData.countryCode
    );
    const len = country?.mobileLength || 10;

    if (!/^\d+$/.test(formData.mobile) || formData.mobile.length !== len) {
      errors.mobile = `Mobile must be ${len} digits`;
      valid = false;
    }

    // Email validation
if (!formData.email.trim()) {
  errors.email = "Email is required";
  valid = false;
} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
  errors.email = "Enter a valid email address";
  valid = false;
}


    setFormErrors(errors);
    return valid;
  };

  /* ---------------- API SUBMIT ---------------- */

  const confirmCall = async () => {
    if (!validateForm()) {
      showToast(
        <>
          <i className="bi bi-exclamation-triangle" /> Please fix the form errors
        </>
      );
      return;
    }

 const payload = {
      scheduled_full_name: formData.fullName.trim(),
      scheduled_email: formData.email.trim(),
      scheduled_subject: formData.subject.trim(),
      scheduled_mobile_code: Number(formData.countryCode.replace("+", "")),
      scheduled_mobile_number: Number(formData.mobile),
      scheduled_date: formatDateForBackend(selectedDate),
      scheduled_time: `${formatTimeForBackend(selectedTime)}:00`,
    };


    try {
      await createStrategyCall(payload);

      gsap.to(sheetRef.current, {
        y: 120,
        opacity: 0,
        duration: 0.3,
        onComplete: closeStrategy,
      });

      showToast(
        <>
          <i className="bi bi-check2-all" /> Strategy call booked successfully!
        </>
      );
    } catch (err) {
      console.error("Strategy Call Error:", err);
      showToast(
        <>
          <i className="bi bi-x-circle" /> Booking failed. Try again.
        </>
      );
    }
  };

  /* ---------------- FORM HANDLERS ---------------- */

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      setFormData((p) => ({ ...p, mobile: value.replace(/\D/g, "") }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleCountryCodeChange = (e) => {
    setFormData((p) => ({
      ...p,
      countryCode: e.target.value,
      mobile: "",
    }));
  };

  const getSelectedCountryMobileLength = () =>
    COUNTRY_CODES.find((c) => c.code === formData.countryCode)
      ?.mobileLength || 10;

 const isFormValid =
  formData.fullName &&
  formData.email &&
  formData.subject &&
  formData.mobile.length === getSelectedCountryMobileLength();


  /* ---------------- UI ---------------- */

  return (
    <div className="strategy-overlay">
      <div className="strategy-sheet large" ref={sheetRef}>
        <button
          className="modal-close-btn"
          onClick={closeAll}
          type="button"
        >
          <i className="bi bi-x"></i>
        </button>

        <div className="strategy-content">
          {/* LEFT PANEL - Always visible */}
          <div className="strategy-left" ref={leftPanelRef}>

            {/* ALWAYS VISIBLE */}
            <h4 className="left-panel-title">PROFESSIONAL CONSULTING</h4>
            <h4 className="session-title">30-Minute Strategy Session</h4>
            <p className="session-subtitle">
              Personalized consultation with our top industry experts to scale your business.
            </p>

            {/* COLLAPSIBLE CONTENT */}
            <div
              ref={leftBodyRef}
              className={`panel-body ${showMore ? "expanded" : "collapsed"}`}
            >
              <div className="left-info-details">
                <div className="info-card">
                  <strong>Virtual Meeting</strong>
                  <p>Zoom / Google Meet link sent after booking</p>
                </div>

                <div className="info-card">
                  <strong>What we'll cover:</strong>
                  <ul>
                    <li>Your business goals & challenges</li>
                    <li>Technology assessment & gaps</li>
                    <li>Relevant solution walkthrough</li>
                    <li>Q&A and next steps</li>
                  </ul>
                </div>

                <div className="timezone">
                  <i className="bi bi-globe" /> India Standard Time (IST)
                </div>
              </div>
            </div>

            {/* SHOW MORE — INSIDE BOX */}
            {canExpand && (
              <button
                className="toggle-more inside-box"
                onClick={() => setShowMore((p) => !p)}
                type="button"
              >
                {showMore ? "Show Less ▲" : "Show More ▼"}
              </button>
            )}
          </div>


          {/* RIGHT PANEL */}
          <div className="strategy-right" ref={rightPanelRef}>
            <div className="panel-body">
              {step === "select" && (
                <Calendar
                  selectedTime={selectedTime}
                  onTimeSelect={setSelectedTime}
                  onDateChange={() => setSelectedTime("")}
                  onApply={(date) => {
                    setSelectedDate(date.toLocaleDateString("en-GB"));
                    setStep("form");
                  }}
                />
              )}

            {/* STEP 2 — FORM ONLY */}
              {step === "form" && (
                <>
                  <button
                    className="back-btn"
                    onClick={() => setStep("select")}
                    type="button"
                  >
                    ← Back to date & time
                  </button>

                  <h4 className="form-title">Client Information</h4>
                  <p className="selected-date-info">
                    <strong>Session Details:</strong> {selectedDate} at {selectedTime}
                  </p>

                 <form className="booking-form" onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      name="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      required
                    />
                    {formErrors.fullName && (
                      <span className="error-message">
                        {formErrors.fullName}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                    />
                    {formErrors.email && (
                      <span className="error-message">
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Subject *</label>
                    <div className="subject-input-wrapper">
                      <input
                        name="subject"
                        placeholder="What would you like to discuss?"
                        value={formData.subject}
                        onChange={handleFormChange}
                        maxLength="50"
                        required
                      />
                      <span className="char-count">
                        {formData.subject.length}/50
                      </span>
                    </div>
                    {formErrors.subject && (
                      <span className="error-message">
                        {formErrors.subject}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <div className="mobile-input-combo">
                      <select
                        value={formData.countryCode}
                        onChange={handleCountryCodeChange}
                        className="country-code-select"
                      >
                        {COUNTRY_CODES.map((item) => (
                          <option key={item.code + item.country} value={item.code}>
                            {item.country} ({item.code})
                          </option>
                        ))}
                      </select>

                      <div className="mobile-input-wrapper">
                        <input
                          name="mobile"
                          placeholder="Enter your mobile number"
                          value={formData.mobile}
                          onChange={handleFormChange}
                          maxLength={getSelectedCountryMobileLength()}
                          required
                        />
                      </div>
                    </div>
                    {formErrors.mobile && (
                      <span className="error-message">
                        {formErrors.mobile}
                      </span>
                    )}
                  </div>


                  <button
                    className="confirm-btn"
                    disabled={!isFormValid}
                    onClick={confirmCall}
                    type="button"
                  >
                    Confirm Booking 
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default StrategyCallModal;